/**
 * Localized texts for the English language.
 *
 * We follow American (en-us) language conventions.
 *
 * Please maintain alphabetical order on variable name for ease of maintenance!
 */

import type { Translation } from './nb';

export const en = {
  apiDocumentation: 'API documentation',
  apiDocVardef: 'Variable definitions',
  apiDocKlass: 'Classifications',
  appSubTitle: ['Here we collect information about our datasets, variables, classifications and APIs in one place.'],
  appTitle: 'SSB Dataportal',

  authentication: {
    logIn: 'Log in',
    logInSsbEmployee: 'Log in as Statistics Norway employee',
    logOut: 'Log out',
    loginHeading: 'Login',
    loginInfo:
      'Login is only available to employees at Statistics Norway. You can still use the service without logging in.',
  },

  breadcrumbsLabel: 'Breadcrumbs',

  button: {
    removeFilter: 'Clear filters',
  },

  by: 'by',

  classification: {
    about: {
      custodian: 'Maintainer',
      mail: 'Mail',
      publishedLanguages: 'Published languages',
      basedOn: 'Based on',
      langEN: 'English',
      langNB: 'Norwegian (Bokmål)',
      langNN: 'Norwegian (Nynorsk)',
      langNO: 'Norwegian',
      legalBasis: 'Legal basis',
      publications: 'Publications',
      unitTypes: 'Unit types',
      noChanges: 'No descriptions for changes within this version.',
      changelog: 'Changelog',
      date: 'Date',
      comment: 'Comment',
    },
    filterLevels: 'Filter by level',
    labelPlural: 'Classifications',
    labelSingular: 'Classification',
    label: 'Classification',
    language: {
      notSelectedLanguage: 'This classification is not available in the selected language',
      missingInSelectedLanguage:
        'This classification is missing content in the selected language, please select another language.',
      contentChangelog: 'The changelog is only available in Norwegian',
    },
    type: 'Type',
    codelist: 'Codelist',
    standard: 'Classification',
    emailPlaceholder: 'Your email address',
    subscribe: 'Subscribe',
    subscribeMessageError: 'An error occurred during registration',
    subscribeMessageAlready: 'You are already subscribed',
    subscribeSubmit: 'Submit',
    subscribeInfo: 'Subscribe to updates for this classification',
    subscribeMessageSuccess:
      'You will receive an email. Follow the instructions in the email to confirm your subscription.',
    subscribeMessageInvalidEmail: 'Enter a valid email address',
    download: {
      button: 'Download',
      formatLabel: 'Format',
      languageLabel: 'Language',
      levelLabel: 'Level',
      allLevels: 'All levels',
      headingCodes: 'Download codes to file',
      headingChanges: 'Download changes to file',
      headingCorrespondence: 'Download correspondence to file',
      copyLink: 'Copy link',
      linkCopied: 'Link copied.',
      confirm: 'Download file',
      inProgress: 'Downloading ...',
      error: 'Could not download the file. Please try again.',
    },
    correspondence: {
      heading: 'Correspondences',
      info: 'Correspondence tables show the relationship between two different code lists.',
      none: 'This version has no correspondences',
      fromLevel: 'Level',
      toLevel: 'Level',
      owner: 'Owner',
      id: 'ID',
      ownerSection: 'Owner section',
      responsible: 'Responsible',
      codeSummary:
        'The correspondence links {sourceCount} codes from “{sourceName}” to {targetCount} codes from “{targetName}”.',
      tableLabel: 'Correspondence table',
      noTarget: 'No corresponding code',
    },
    variant: {
      id: 'ID',
      description: 'Description',
      responsible: 'Responsible',
      ownerSection: 'Owner section',
      noVariants: 'This version has no variants',
      numberOfCodesAndLevels: 'The variant contains {numberOfCodes} codes distributed over {numberOfLevels} {level}.',
      variantHeading: 'Variants',
      variantInfo:
        "A variant is based on a classification version and represents an alternative grouping, an expansion, or a reduction of it's codes.",
    },
  },
  classificationDetails: {
    codes: 'Codes',
    details: 'Details',
    changes: 'Changes',
    versions: 'Select version',
    correspondences: 'Correspondences',
    variants: 'Variants',
  },
  close: 'Close',
  codeTree: {
    label: 'Code list',
    expand: 'Show subcodes for',
    collapse: 'Hide subcodes for',
    selectCode: 'Select code',
    filterLabel: 'Filter by code or name',
    filterPlaceholder: 'Filter by code or name',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    codeColumn: 'Code',
    nameColumn: 'Name',
    back: 'Back',
    notesButtonLabel: 'Show additional information for',
    showShortNames: 'Show short names',
    shortNameColumn: 'Short name',
    notesColumn: 'Additional information',
  },
  codeSnippet: {
    codeExample: 'Example code',
    daplaManual: 'Dapla manual',
    getVariableDefinition: 'Get variable definition with',
    linkToPyPiPackage: 'dapla-toolbelt-metadata (pypi.org)',
    daplaLab: 'Dapla Lab',
  },

  cookieBanner: {
    closeButtonLabel: 'Close button',
    label: 'Cookie banner',
    message: 'We store cookies necessary to keep the website working and secure.',
  },
  contact: {
    label: 'Contact',
  },
  copy: {
    code: 'Copy code',
    copied: 'Copied',
    id: 'Copy ID',
    shortName: 'Copy short name',
    filePath: 'Copy gs:// path',
  },

  dataCoverageTimeline: {
    availableDataFileLabel: 'related data file',
    availableDataFileLabelPlural: 'related data files',
    filePathLabel: 'File path',
    labelBimesterPrefix: 'B',
    labelFullYear: 'Full year',
    labelHalfYearPrefix: 'H',
    labelQuarterPrefix: 'Q',
    labelTriannualPrefix: 'T',
    monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    statusDataPresent: 'Data available',
    statusMissingTargetSegment: 'No file with data for this period',
    tooltipStatusDataPresent: 'Data file exists',
    tooltipStatusMissingTargetSegment: 'No file with data for this period',
  },
  dataProduct: {
    labelPlural: 'Data products',
  },
  dataProductDetail: {
    dataProductFilters: 'Data product filters',
    dataset: 'Dataset',
    sortByMostNamingStandardViolations: 'Most naming standard violations',
    noDatasetAvailable: 'No dataset available',
  },
  datasetDetail: {
    aboutDataset: 'About dataset',
    dataProduct: 'Data product',
    bucket: 'Bucket',
    datasetState: 'Dataset state',
    assessment: 'Assessment',
    responsible: 'Responsible team',
    id: 'ID',
    namingStandardViolations: 'Number of naming standard violations',
    dataFiles: 'Data files',
    dataCoverageTimeline: 'Available periods',
  },
  editing: {
    created: 'Created',
    updated: 'Last updated',
  },

  error: {
    classificationDetailsTabs: {
      notFoundCodes: 'Codes not found',
      notFoundAboutClassification: 'About classification not found',
      notFoundChanges: 'Code changes not found',
      notFoundVersions: 'Versions not found',
      notFoundCorrespondences: 'Correspondences not found',
      notFoundVariants: 'Variants not found',
    },
    somethingWentWrong: 'Sorry, something went wrong.',
    unauthorized: 'Sorry, you do not have access.',
    technicalProblemsTitle: 'We are experiencing technical problems',
    technicalProblemsMessage: 'This is not caused by anything you did. Wait a moment and try again.',
    reloadPage: 'Reload page',
    goBack: 'Go back',
    goHome: 'Go to home page',
    helpTitle: 'You can try to:',
    helpReload: 'wait a little and reload the page',
    helpBack: 'go back to the previous page',
    helpHome: 'go to the home page',
    helpChangeFilters: 'change search or filters',
    helpRegisterChangeTable: 'register a change table',
    missingChangeTable: '{sourceName} has no change table with: {targetName}',
    statusCode: 'Error code',
    referenceCode: 'Reference',
    supportPrefix: 'If the problem has persisted for a while, you can',
    supportLinkText: 'contact us',
    brokenLinkMailSubject: 'Broken link in SSB Dataportal',
    brokenLinkMailBody: (path: string) =>
      [
        'Hi, I want to report a possible broken link in SSB Dataportal.',
        '',
        `Page: ${path}`,
        '',
        'Description of what did not work:',
      ].join('\n'),
    notFoundDataProductDetails: 'Data product not found',
    notFoundTitleDataProductDetails: 'Data product not found',
    notFoundMessageDataProductDetails: 'Is there a typo in the URL? Or has the data product been deleted or moved?',
    notFoundHelpListDataProductDetails: [
      'check that you have the correct data product ID in the URL',
      'go to the data product overview',
      'go to the home page',
    ],
    notFoundTitleDataset: 'Dataset not found',
    notFoundMessageDataset: 'Is there a typo in the URL? Or has the dataset been deleted or moved?',
    notFoundHelpListDataset: [
      'check that you have the correct dataset ID in the URL',
      'go to the dataset overview',
      'go to the data product overview',
    ],
    notFoundHelpListClassificationDetails: [
      'check that you have the correct classification ID in the URL',
      'go to the classification overview',
      'go to the home page',
    ],
    notFoundHelpListClassificationId: [
      'check that you have the correct classification ID in the URL',
      'go to the classification overview',
    ],
    notFoundTitleClassificationDetails: 'Classification not found',
    notFoundMessageClassificationDetails:
      'Is there a typo in the URL? Or has the classification been deleted or moved?',
    notFoundTitleVersionDetails: 'Version not found',
    notFoundMessageVersionDetails: 'Is there a typo in the URL? Or has the version been deleted or moved?',
    notFoundHelpListVersionDetails: [
      'check that you have the correct version ID in the URL',
      'go back to the classification',
      'go to the classification overview',
    ],
    notFoundTitleVariantDetails: 'Variant not found',
    notFoundMessageVariantDetails: 'Is there a typo in the URL? Or has the variant been deleted or moved?',
    notFoundHelpListVariantDetails: [
      'check that you have the correct variant ID in the URL',
      'go to the variants overview',
      'go to the classification overview',
    ],
    notFoundTitleCorrespondenceDetails: 'Correspondence table not found',
    notFoundMessageCorrespondenceDetails:
      'Is there a typo in the URL? Or has the correspondence table been deleted or moved?',
    notFoundHelpListCorrespondenceDetails: [
      'check that you have the correct correspondence table ID in the URL',
      'go to the correspondences overview',
      'go to the classification overview',
    ],
    notFoundTitle: 'Page not found',
    notFoundMessage: 'The page may have been moved, deleted, or the URL may be incorrect.',
    notFoundTitleVariableDetails: 'Variable definition not found',
    notFoundMessageVariableDetails: 'Is there a typo in the URL? Or has the variable definition been deleted or moved?',
    notFoundHelpListVariableDetails: [
      'check that you have the correct variable definition short name in the URL',
      'go to the variable definition overview',
      'go to the home page',
    ],
    reportBrokenLink: 'Report broken link',
  },
  from: 'From',
  info: {
    aboutDataportal: {
      body: [
        "The goal is to make it easier to find, understand and use SSB's data correctly and efficiently.",
        'In this first version you can explore Statistics Norway\'s variable definitions. The portal will gradually expand with overviews of datasets, classifications and APIs, so you can see the full chain from concept to finished data file. By connecting definitions from Vardef with codes from Klass and dataset documentation, we establish a "single source of truth" that ensures we always interpret and use our data the same way.',
      ],
      title: 'About SSB Dataportal',
      toggle: 'Why SSB Dataportal',
    },
    datasetPrototypeIntro: 'Under development',
    datasetPrototypeInfo:
      'This page describes a selection of data at Statistics Norway. Development continues with more data types and more detailed descriptions.',
    feedbackTitle: 'Share your experience!',
    feedbackBody: 'We continuously work to improve the Dataportal, and you can help us by filling out the',
    feedbackForm: 'feedback form for the SSB Dataportal',
    footerAboutPage: 'About the website',
    footerContact: 'Get in touch',
    footerAccessibilityStatement: 'Accessibility statement',
    footerPrivacyStatement: 'Privacy statement',
    landingPageSubTitle: "Your gateway to Statistics Norway's public data",
  },
  loading: {
    filters: 'Loading filters...',
    results: 'Loading results...',
  },
  migrationClassifications: {
    header: 'Klass has been moved from ssb.no/klass',
    info: 'You are now on the new Klass service in SSB Dataportal. Please update your bookmarks and saved links.',
  },
  migrationVariableDefinitions: {
    header: 'Migration of variable definitions in progress',
    info: 'We are moving variable definitions to SSB Dataportal. Until the work is complete, some content will still be on the old site.',
    linkText: 'Find more variable definitions on ssb.no',
  },
  navigateHome: 'Navigate to home page',
  next: 'Next',
  no: 'No',
  noDataPlaceholder: '—',
  on: 'on',
  opensInNewTab: 'opens in a new tab',
  owner: {
    daplaTeam: 'Dapla Team',
    groups: 'Groups',
  },
  pageTitle: {
    classifications: 'Classifications',
    dataProducts: 'Data products',
    variableDefinitions: 'Variable definitions',
  },
  previous: 'Previous',
  products: {
    assessment: {
      filterLabel: 'Value assessment',
      open: 'Open',
      protected: 'Protected',
      sensitive: 'Sensitive',
    },
    datasetState: {
      processedData: 'Processed data',
      outputData: 'Output data',
      inputData: 'Input data',
      statistics: 'Statistics',
      sourceData: 'Source data',
    },
    other: 'Other data product',
    statistic: 'Statistical product',
    storageCategory: {
      filterLabel: 'Storage category',
      shared: 'Shared',
      product: 'Product',
      source: 'Source',
    },
    typeFilterLabel: 'Data product type',
    unknown: 'Unknown product type',
  },
  search: {
    hits: 'hits',
    label: 'Search',
    noHits: 'Your search returned no hits',
    variableDefinitions: 'Variable definition search hits',
    classifications: 'Classification search hits',
    textFilter: {
      label: 'Filter by name',
      tagLabel: 'Name:',
      inputId: 'search-input',
    },

    filter: {
      close: 'Close filter',
      label: 'Filter',
      filterAndSearch: 'Filter and search',
      open: 'Open filter',
    },

    sort: {
      label: 'Sorting',
      lastUpdatedFirst: 'Last updated first',
      titleAlphabeticalAsc: 'Title A-Z',
      titleAlphabeticalDesc: 'Title Z-A',
    },
  },

  filterTag: {
    remove: 'Remove',
    listLabel: 'List of selected filters',
    sectionLabel: 'Active search filters',
  },

  ssbDataportal: 'SSB Dataportal',
  statisticsNorway: 'Statistics Norway',

  status: {
    draft: 'Draft',
    label: 'Status',
    publishedExternal: 'Published externally',
    publishedInternal: 'Published internally',
  },

  subjectArea: 'Subject area',
  subjectFields: 'Subject areas',

  tabs: {
    ariaLabel: 'Catalog navigation',
    classifications: 'Classifications',
    dataProducts: 'Data products',
    variableDefinitions: 'Variable definitions',
  },

  to: 'To',
  unitTypes: 'Unit types',
  yes: 'Yes',

  validity: {
    validityPeriods: 'Validity periods',
    chooseValidityPeriod: 'Select validity period',
    validityPeriodsInfo:
      'When a variable definition changes meaning time periods can be set for each distinct meaning. A specific validity period may be chosen below by selecting the date from which it is valid.',
    validFrom: 'Valid from',
    validTo: 'Valid to',
  },

  variableDefinition: {
    aboutVariable: 'About the variable',
    comment: 'Comment',
    contact: 'Contact',
    documentation: 'Documentation',
    externalPersonalData: 'Sensitive personal data',
    id: 'ID',
    internalPersonalData: 'Contains special categories of personal data',
    labelPlural: 'Variable definitions',
    mail: 'Email',
    owner: 'Owner',
    unitTypeInfo:
      'A unit type is the type of objects (units) that statistics are produced for, for example person, enterprise, and election',
    shortName: 'Short name',
  },
  versions: {
    name: 'Name',
    invert: 'Invert table',
    isNotValid: "This version is not valid on today's date.",
    noChanges: 'No changes to codes for this version',
    numberOfCodesAndLevels: 'This version contains {numberOfCodes} codes organized into {numberOfLevels} {level}.',
    level: 'level',
    levelPlural: 'levels',
    codeChangesForVersion: '{numberOfChanges} changes to codes from the previous version.',
    codeChangesTableLabel: 'Code changes table',
  },
} satisfies Translation;
